import { useCallback, useEffect, useState } from 'react'

import { NetworkStatus } from '@apollo/client'
import { ActionIcon, Input, Loader, Modal } from '@mantine/core'
import { useDebouncedValue } from '@mantine/hooks'
import { useInView } from 'react-intersection-observer'

import { navigate, routes } from '@redwoodjs/router'
import { useQuery } from '@redwoodjs/web'

import DNButton from 'src/components/Buttons/Button/DNButton'
import StyledBadge from 'src/components/DataDisplay/StyledBadge/StyledBadge'
import { useEmailModalContext } from 'src/context/EmailModalContext'
import {
  usePhoneDevice,
  usePhoneDeviceDispatch,
} from 'src/context/PhoneDeviceContext'
import { SEARCH_APPLICANTS } from 'src/graphql/applicants.graphql'
import IconEmailSimple from 'src/icons/IconEmailSimple'
import IconMessage from 'src/icons/IconMessage'
import IconPhoneCall from 'src/icons/IconPhoneCall'
import {
  buildFullName,
  calculateStageBadgeColor,
  formatSnakeValue,
} from 'src/lib/formatters'
import { PhoneDeviceActionType } from 'src/lib/phone-device.reducer'
import {
  formatInternationalPhoneNumber,
  unformatPhoneNumber,
} from 'src/lib/phone.utils'
import { isValidEmail } from 'src/lib/email.utils'
import { isPhoneLikeInput } from 'src/lib/phone.utils'

import { toast } from '../Toast/Toast'

function isAlphabetic(str) {
  return /^[a-zA-Z]+$/.test(str)
}

function isNumeric(str) {
  return /^\d+$/.test(str)
}

const SearchModal = ({ opened, close, onCreateApplicant }) => {
  const [search, setSearch] = useState('')
  const [debouncedSearch] = useDebouncedValue(search, 300)
  const dispatchPhoneAction = usePhoneDeviceDispatch()
  const { handleOpen: openEmailModal } = useEmailModalContext()

  const phoneDeviceContext = usePhoneDevice()
  const resultsPerPage = 10
  const page = 1

  const { ref, inView } = useInView({
    /* Optional options */
    threshold: 0,
  })

  const minSearchLength = 2

  const {
    data: applicantsData,
    loading,
    fetchMore,
    networkStatus,
  } = useQuery(SEARCH_APPLICANTS, {
    skip: debouncedSearch.length < minSearchLength,
    notifyOnNetworkStatusChange: true,
    variables: {
      pageInfo: {
        offset: (page - 1) * resultsPerPage,
        limit: resultsPerPage,
      },
      filters: {
        searchText: debouncedSearch,
      },
    },
    onError: () => {
      toast('Unable to get applicants, please try again.', 'error')
    },
  })

  const applicants = applicantsData?.applicants?.items || []

  useEffect(() => {
    if (inView) {
      fetchMore({
        variables: {
          pageInfo: {
            offset: applicants.length ?? 0,
            limit: resultsPerPage,
          },
        },
      })
    }
  }, [inView])

  const onPhoneCall = useCallback(
    async ({ applicantId, applicantFullName, phone }) => {
      if (phoneDeviceContext?.phoneDevice?.isBusy) {
        return
      }
      dispatchPhoneAction({
        type: PhoneDeviceActionType.CALL,
        payload: {
          applicantId,
          phone,
          applicantFullName,
        },
      })
    },
    []
  )

  return (
    <Modal
      opened={opened}
      onClose={close}
      yOffset={100}
      overlayProps={{
        backgroundOpacity: 0.2,
        blur: 5,
      }}
      size="50%"
      withCloseButton={false}
      classNames={{
        body: 'py-2 px-0',
      }}
    >
      <div className="flex flex-row items-center gap-2">
        <Input
          variant="unstyled"
          placeholder="Search applicants"
          value={search}
          onChange={(event) => setSearch(event.currentTarget.value)}
          className="flex-1 px-4"
          size="lg"
          rightSection={loading && <Loader size="xs" color="gray" />}
        />
        {applicants.length === 0 &&
          !loading &&
          debouncedSearch.length >= minSearchLength && (
            <div className="group flex flex-row justify-end gap-1 pr-4">
              <DNButton
                variant="secondary"
                onClick={() => {
                  close()
                  const trimmed = debouncedSearch.trim()
                  const payload: { phone?: string; name?: string; email?: string } =
                    {}
                  if (isValidEmail(trimmed)) {
                    payload.email = trimmed
                  } else if (isPhoneLikeInput(trimmed)) {
                    // Handle common phone formats like (555) 123-4567, 555-123-4567, +1 555 123 4567
                    payload.phone = trimmed
                  } else if (isAlphabetic(trimmed)) {
                    payload.name = trimmed
                  }
                  onCreateApplicant(payload)
                }}
              >
                Create Applicant
              </DNButton>
            </div>
          )}
      </div>
      {search.length > 0 && debouncedSearch.length < minSearchLength && (
        <div className="px-4 py-3 text-sm text-doubleNickel-gray-500">
          Type at least {minSearchLength} characters to search
        </div>
      )}
      {debouncedSearch.length >= minSearchLength &&
        !loading &&
        applicants.length === 0 && (
          <div className="px-4 py-3 text-sm text-doubleNickel-gray-500">
            No applicants found
          </div>
        )}
      {debouncedSearch.length >= minSearchLength &&
        applicants.map((applicant) => (
        <a
          key={applicant.applicantId}
          className="group flex flex-row items-center gap-1 px-4 py-3 hover:bg-doubleNickel-gray-100"
          target="_blank"
          href={`/applicants/${applicant.applicantId}`}
          rel="noreferrer"
        >
          <div className="flex flex-col gap-1">
            <div className="flex flex-row gap-2 text-base font-medium text-doubleNickel-gray-900">
              {buildFullName(applicant)}
              <StyledBadge
                color={calculateStageBadgeColor(applicant.applicantStage)}
                textTransform="capitalize"
              >
                {applicant.applicantStage === 'PROSPECT'
                  ? formatSnakeValue(applicant.hiringStage)
                  : formatSnakeValue(applicant.applicantStage)}
              </StyledBadge>
            </div>

            <div className="text-xs text-doubleNickel-gray-500">
              {applicant.jobListing?.title} ·{' '}
              {formatInternationalPhoneNumber(applicant.phone)} ·{' '}
              {applicant.yearsOfExperience
                ? applicant.yearsOfExperience + ' years of experience'
                : 'No experience information available'}{' '}
              · {applicant.callCount} calls made
            </div>
          </div>
          <ActionIcon
            variant="subtle"
            color="gray"
            className={'ml-auto opacity-0 group-hover:opacity-100'}
            onClick={(e) => {
              e.stopPropagation()
              e.preventDefault()
              onPhoneCall({
                applicantId: applicant.applicantId,
                applicantFullName: buildFullName(applicant),
                phone: unformatPhoneNumber(applicant.phone),
              })
              close()
            }}
          >
            <IconPhoneCall className="h-4 w-4 fill-none stroke-doubleNickel-gray-700" />
          </ActionIcon>
          <ActionIcon
            variant="subtle"
            color="gray"
            className={'opacity-0 group-hover:opacity-100'}
            onClick={(e) => {
              e.stopPropagation()
              e.preventDefault()
              navigate(
                routes.applicantDetails({
                  id: applicant.applicantId,
                  tab: 'activitylog',
                  activityType: 'SMS',
                })
              )
              close()
            }}
          >
            <IconMessage className="h-4 w-4 fill-none stroke-doubleNickel-gray-700" />
          </ActionIcon>
          {applicant.email && (
            <ActionIcon
              variant="subtle"
              color="gray"
              className={'opacity-0 group-hover:opacity-100'}
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
                openEmailModal({
                  applicantId: applicant.applicantId,
                  defaultTo: applicant.email,
                })
                close()
              }}
            >
              <IconEmailSimple className="h-4 w-4 fill-none stroke-doubleNickel-gray-700" />
            </ActionIcon>
          )}
        </a>
      ))}
      {debouncedSearch.length >= minSearchLength &&
        networkStatus === NetworkStatus.fetchMore && (
          <Loader
            size="sm"
            className="flex w-full items-center justify-center "
          />
        )}
      {debouncedSearch.length >= minSearchLength &&
        applicants.length < applicantsData?.applicants.totalCount &&
        !loading && (
          <div ref={ref} className="opacity-0">
            Inview placeholder
          </div>
        )}
    </Modal>
  )
}

export default SearchModal
